import React, { useState } from 'react';

/**
 * Formatea un número como precio con separadores de miles y centavos más pequeños.
 * Separa la parte entera y los centavos para mejor legibilidad.
 * @param {number} numero - El número a formatear
 * @returns {Object} Objeto con parte entera y centavos formateados
 */
const formatearPrecio = (numero) => {
  const numeroStr = numero.toFixed(2);
  const [parteEntera, centavos] = numeroStr.split('.');
  
  // Agrega separadores de miles con puntos
  const parteEnteraFormateada = parseInt(parteEntera).toLocaleString('es-AR');
  
  return {
    entera: parteEnteraFormateada,
    centavos: centavos
  };
};

/**
 * Componente de tarjeta de producto - Estilo Minimalista Premium
 * @param {Object} producto - Datos del producto
 * @param {Function} onBuy - Función para iniciar compra
 * @param {boolean} loading - Estado de carga del botón
 * @param {boolean} featured - Indica si es un producto destacado
 */
const ProductCard = ({ producto, onBuy, loading = false, featured = false }) => {
  const [cantidad, setCantidad] = useState(1);

  const handleCantidadChange = (e) => {
    const valor = parseInt(e.target.value);
    if (!isNaN(valor) && valor >= 1 && valor <= 99) {
      setCantidad(valor);
    }
  };

  const handleCantidadBlur = () => {
    if (cantidad < 1 || !Number.isInteger(cantidad)) {
      setCantidad(1);
    }
  };

  const montoTotal = producto.precio * cantidad;
  const precioUnitario = producto.precio;
  
  // Formatea el precio total para mostrar con separadores de miles y centavos pequeños
  const precioFormateado = formatearPrecio(montoTotal);
  const precioUnitarioFormateado = formatearPrecio(precioUnitario);

  const handleBuyClick = () => {
    onBuy(producto, cantidad);
  };

  return (
    <div className="relative bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-neutral-200 dark:border-neutral-700 group overflow-hidden">
      
      {/* Patrón de fondo sutil */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10" 
           style={{
             backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
             backgroundSize: '24px 24px'
           }} />
      
      <div className="relative z-10">
        {/* Header con imagen y precio */}
        <div className="flex justify-between gap-4 mb-4">
          {/* Imagen con estilo moderno */}
          <div className="relative flex-shrink-0">
            {producto.imagen_url ? (
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white dark:bg-neutral-800 shadow-md border border-neutral-200 dark:border-neutral-600">
                <img
                  src={producto.imagen_url}
                  alt={producto.nombre}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-rose-100 to-red-100 dark:from-rose-900/30 dark:to-red-900/30 flex items-center justify-center border border-rose-200 dark:border-rose-800">
                <span className="text-3xl">🎁</span>
              </div>
            )}
          </div>

          {/* Información de precio */}
          <div className="flex flex-col items-end justify-center">
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1 font-medium">
              Precio unitario
            </div>
            <div className="text-sm text-neutral-600 dark:text-neutral-300 mb-2">
              ${precioUnitarioFormateado.entera}<sup className="text-xs">{precioUnitarioFormateado.centavos}</sup>/u
            </div>
            <div className="text-4xl font-bold text-neutral-900 dark:text-white">
              ${precioFormateado.entera}<sup className="text-xl text-neutral-500 dark:text-neutral-400">{precioFormateado.centavos}</sup>
            </div>
          </div>
        </div>

        {/* Título y descripción */}
        <h3 className="text-xl font-bold text-neutral-900 dark:text-white">
          {producto.nombre}
        </h3>

        {producto.descripcion && (
          <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed mb-5">
            {producto.descripcion}
          </p>
        )}

        {/* Controles */}
        <div className="flex gap-3">
          {/* Selector de cantidad */}
          <div className="flex items-center bg-white dark:bg-neutral-800 rounded-2xl p-1.5 shadow-sm border border-neutral-200 dark:border-neutral-600 flex-1 gap-1">
            <button
              onClick={() => setCantidad(prev => Math.max(1, prev - 1))}
              disabled={cantidad <= 1}
              className="w-11 h-11 flex items-center justify-center text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 font-bold text-lg"
            >
              −
            </button>
            <input
              type="number"
              min="1"
              max="99"
              value={cantidad}
              onChange={handleCantidadChange}
              onBlur={handleCantidadBlur}
              className="flex-1 h-11 text-center text-lg font-bold text-neutral-900 dark:text-white border-0 focus:ring-0 bg-transparent"
            />
            <button
              onClick={() => setCantidad(prev => Math.min(99, prev + 1))}
              disabled={cantidad >= 99}
              className="w-11 h-11 flex items-center justify-center text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 font-bold text-lg"
            >
              +
            </button>
          </div>

          {/* Botón de donación */}
          <button
            onClick={handleBuyClick}
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800 text-white rounded-2xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <>
                <span className="text-sm font-medium">Donar</span>
                <svg 
                  className="w-5 h-5" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
